import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, Radio, Sparkles } from "lucide-react";
import { PawIcon, apiFetch } from "@/lib/brand";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id") || "";
    const [state, setState] = useState("checking"); // checking | paid | pending

    useEffect(() => {
        if (!sessionId) {
            setState("pending");
            return;
        }
        let tries = 0;
        let timer;
        const poll = async () => {
            try {
                const d = await apiFetch(`/payments/status/${sessionId}`);
                if (d.payment_status === "paid") {
                    setState("paid");
                    return;
                }
            } catch {
                /* keep polling */
            }
            tries += 1;
            if (tries < 8) timer = setTimeout(poll, 2000);
            else setState("pending");
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16 relative" data-testid="payment-success-page">
            <div className="absolute inset-0 bg-radial-purp" />
            <div className="relative w-full max-w-lg rounded-3xl bg-panel/80 backdrop-blur-md border border-purp/35 card-glow p-8 md:p-12 text-center">
                <span className="mx-auto w-14 h-14 rounded-2xl bg-obsidian border border-purp/40 flex items-center justify-center">
                    {state === "paid" ? (
                        <Check className="w-7 h-7 text-volt" />
                    ) : state === "checking" ? (
                        <Loader2 className="w-7 h-7 text-purp animate-spin" />
                    ) : (
                        <PawIcon className="w-7 h-7 text-purp" />
                    )}
                </span>
                {state === "paid" ? (
                    <>
                        <h1 className="mt-6 font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]" data-testid="payment-success-heading">
                            Welcome to <span className="italic text-purp text-glow">the Network.</span>
                        </h1>
                        <p className="mt-5 text-slate-400 text-sm md:text-base leading-relaxed">
                            Your PURP ALERT NETWORK Membership — $4.99 USD/month, recurring monthly subscription — is
                            active. A welcome email is on its way with your billing details. You can cancel anytime
                            to stop future charges.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                            <Link
                                to="/#radio"
                                data-testid="payment-success-radio-link"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)]"
                            >
                                <Radio className="w-4 h-4" /> Press Play on Radio
                            </Link>
                            <Link
                                to="/pan-school"
                                data-testid="payment-success-explore-link"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-purp/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-purp-neon hover:bg-purp/10 transition-colors"
                            >
                                <Sparkles className="w-4 h-4" /> Explore the Network
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        <h1 className="mt-6 font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]" data-testid="payment-pending-heading">
                            {state === "checking" ? "Confirming your payment" : "Almost there."}
                        </h1>
                        <p className="mt-5 text-slate-400 text-sm md:text-base leading-relaxed">
                            {state === "checking"
                                ? "Hold tight — we're confirming your membership with our payment provider."
                                : "If you completed checkout, your membership is processing and a welcome email lands in your inbox shortly. Questions: PurpAlertNetwork@proton.me."}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
